import { onMounted, onBeforeUnmount, watch, getCurrentInstance, nextTick, computed } from "vue";
import { usePage } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { useAuthStore } from "@/stores/auth/authStore";

/**
 * Composable for automatically loading component translations
 * - Loads translations on component mount
 * - Reloads translations on route changes
 * - Works with both component instance and manual path specification
 * 
 * @param {Object} options - Configuration options
 * @param {String} options.componentPath - Optional: Manual component path (e.g., 'Pages/Profile/Edit')
 * @param {Boolean} options.watchRoute - Whether to watch for route changes (default: true)
 * @param {Boolean} options.loadGlobal - Whether to load global.json translations (default: true)
 */
export function useComponentTranslations(options = {}) {
  const authStore = useAuthStore();
  // Store instance during setup (getCurrentInstance only works during setup)
  const instance = getCurrentInstance();
  const page = instance ? usePage() : null;
  const url = page
    ? computed(() => {
        const pageUrl = page.url;
        return pageUrl && typeof pageUrl === "object" && "value" in pageUrl
          ? pageUrl.value
          : pageUrl;
      })
    : null;
  
  // Determine component path
  let componentPath = options.componentPath;
  let componentPathWithVue = options.componentPath ? `${options.componentPath}.vue` : null;
  
  // If no path provided, try to infer from component instance
  if (!componentPath && instance) {
    const file = instance?.type?.__file || instance?.__file;
    if (file) {
      const marker = "/resources/js/";
      const idx = file.lastIndexOf(marker);
      if (idx !== -1) {
        const relative = file.substring(idx + marker.length);
        componentPath = relative.replace(/\.(vue|js|ts|tsx)$/i, "").replace(/\\/g, "/");
        componentPathWithVue = `${componentPath}.vue`;
      }
    }
  }
  
  // Store instance for use in async callbacks (getCurrentInstance doesn't work in async)
  const componentInstance = instance;
  
  // Function to load translations
  const loadTranslations = async () => {
    if (!componentPath) {
      console.warn('useComponentTranslations: Could not determine component path');
      return;
    }
    
    // console.log(`[useComponentTranslations] Loading translations for: ${componentPath}`);
    
    try {
      // Load keys from tcontractor.json for this component
      authStore.loadUsedKeysForRoute(componentPath);
      if (componentPathWithVue) {
        authStore.loadUsedKeysForRoute(componentPathWithVue);
      }
      
      // Also try to load with component instance for global.json filtering
      if (options.loadGlobal !== false) {
        await nextTick();
        if (instance) {
          await authStore.loadComponentTranslations(instance, { 
            withGlobal: true, 
            clear: false 
          });
        }
      }
      // console.log(`[useComponentTranslations] Successfully loaded translations for: ${componentPath}`);
    } catch (error) {
      // console.warn(`Failed to load translations for ${componentPath}:`, error);
      // Fallback: just load keys from tcontractor.json
      try {
        authStore.loadUsedKeysForRoute(componentPath);
        if (componentPathWithVue) {
          authStore.loadUsedKeysForRoute(componentPathWithVue);
        }
      } catch (fallbackError) {
        console.warn('Fallback translation loading also failed:', fallbackError);
      }
    }
  };
  
  // Function to reload translations (used by watchers and event listeners)
  const reloadTranslations = async () => {
    if (!componentPath) {
      return;
    }
    
    // console.log(`[useComponentTranslations] Reloading translations for: ${componentPath}`);
    
    authStore.loadUsedKeysForRoute(componentPath);
    if (componentPathWithVue) {
      authStore.loadUsedKeysForRoute(componentPathWithVue);
    }
    
    // Also reload with component instance for global.json filtering
    if (options.loadGlobal !== false && componentInstance) {
      await nextTick();
      try {
        await authStore.loadComponentTranslations(componentInstance, { 
          withGlobal: true, 
          clear: false 
        });
      } catch (error) {
        console.warn('Failed to reload component translations:', error);
      }
    }
    
    // console.log(`[useComponentTranslations] Successfully reloaded translations for: ${componentPath}`);
  };
  
  // Track if component is mounted
  let isMounted = false;
  
  // Inertia event handler
  let inertiaHandler = null;
  
  if (!instance) {
    console.warn("useComponentTranslations should be called from setup().");
    // Fallback behavior: load once for manual componentPath without lifecycle hooks.
    if (componentPath) {
      loadTranslations();
    }
  } else {
    // Load translations on mount
    onMounted(() => {
      isMounted = true;
      loadTranslations();
      
      // Listen to Inertia's success event (fires after navigation completes)
      // This ensures translations reload for all navigation methods (Link clicks, Inertia.visit, etc.)
      if (typeof Inertia?.on === 'function') {
        inertiaHandler = async () => {
          // Only reload if component is still mounted
          if (isMounted && componentPath) {
            // Small delay to ensure component is ready
            await nextTick();
            // Reload translations when navigation completes
            await reloadTranslations();
          }
        };
        
        Inertia.on('success', inertiaHandler);
      }
    });
    
    // Watch for route changes and reload translations (if enabled)
    if (options.watchRoute !== false && url) {
      watch(url, async (newUrl, oldUrl) => {
        // Only reload if URL actually changed and component is mounted
        if (newUrl !== oldUrl && isMounted && componentPath) {
          // On route change, reload keys from tcontractor.json
          await nextTick();
          await reloadTranslations();
        }
      }, { immediate: false });
    }
    
    // Cleanup Inertia listener on unmount
    onBeforeUnmount(() => {
      isMounted = false;
      if (typeof Inertia?.off === 'function' && inertiaHandler) {
        Inertia.off('success', inertiaHandler);
      }
    });
  }
  
  return {
    loadTranslations,
    reloadTranslations,
    componentPath,
    componentPathWithVue
  };
}

